import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, DesktopIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex, Grid, Heading, Text, TextField } from '@radix-ui/themes';
import * as Form from '@radix-ui/react-form';
import { AudiencePlatformItem } from '@digiseg-labs/api-client';
import GenericSelect from './GenericSelect';
import { otherDSPOptions } from '../types';
import { useSubmitAudienceRequest, AudienceRequestPayload } from '../hooks/useSubmitAudienceRequest';

type Props = {
  onBack: () => void;
  onClose?: () => void;
  onPlatformSelected: (platform: AudiencePlatformItem | undefined) => void;
  onSubmit?: (data: any) => Promise<void>;
  platforms: AudiencePlatformItem[];
  audienceName?: string;
  audienceCategory?: string;
  countrySelected?: string;
  selectedPlatform?: AudiencePlatformItem;
  audienceCodes?: string;
  requestType?: 'classic' | 'builder';
  audienceOverview?: string;
  audienceOverviewHtml?: string;
  isCustomRequest?: boolean;
};

const AudienceRequestForm: React.FC<Props> = ({
  onBack,
  onClose,
  onSubmit,
  platforms,
  onPlatformSelected,
  audienceName,
  audienceCategory,
  countrySelected,
  selectedPlatform,
  audienceCodes,
  requestType = 'classic',
  audienceOverview,
  audienceOverviewHtml,
  isCustomRequest,
}) => {
  const [successMessage, setSuccessMessage] = useState('');
  const [platformError, setPlatformError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { submit: submitAudienceRequest } = useSubmitAudienceRequest();
  useEffect(() => {
    setStartDate('');
    setEndDate('');
  }, [audienceName, audienceCategory, isCustomRequest]);

  // YYYY-MM-DD
  const today = new Date().toISOString().split('T')[0];

  return (
    <Form.Root
      className="request-form"
      onSubmit={async (event) => {
        event.preventDefault();
        setSuccessMessage('');
        setPlatformError('');
        const formElement = event.currentTarget;
        const formData = new FormData(formElement);
        const data = Object.fromEntries(formData);
        const adType = ((data.adType as string) || '').trim();
        
        const startEl = formElement.querySelector<HTMLInputElement>('input[name="campaignStartDate"]');
        const endEl = formElement.querySelector<HTMLInputElement>('input[name="campaignEndDate"]');
        startEl?.setCustomValidity('');
        endEl?.setCustomValidity('');

        if (!formElement.checkValidity()) {
          formElement.reportValidity();
          return;
        }

        const platformCode = selectedPlatform?.code;
        if (requestType !== 'builder' && !platformCode) {
          setPlatformError('Please select a platform.');
          return;
        }

        const platformName = requestType === 'builder'
          ? (data.platformCode as string) || ''
          : selectedPlatform?.display_name || platformCode || '';

        if (requestType !== 'builder') {
          data.platformCode = platformCode || '';
        }

        const payload: AudienceRequestPayload = {
          name: (data.name as string) || '',
          email: (data.email as string) || '',
          company: (data.company as string) || '',
          audience_name: (data.audienceName as string) || '',
          audience_category: (data.audienceCategory as string) || '',
          country_selected: (data.countrySelected as string) || '',
          platform_name: platformName,
          campaign_start_date: (data.campaignStartDate as string) || '',
          campaign_end_date: (data.campaignEndDate as string) || '',
          seat_name: (data.seatName as string) || '',
          seat_id: (data.seatId as string) || '',
          ad_type: adType || undefined,
        };

        try {
          await submitAudienceRequest(payload);
        } catch (err) {
          console.error('Failed to submit audience request:', err);
        }

        if (onSubmit) await onSubmit(data);
        setSuccessMessage('Form submitted successfully!');
        setTimeout(() => {
          if (onClose) {
            onClose();
          } else {
            onBack();
          }
        }, 500);
      }}
    >
      {isCustomRequest ? (
        <>
          <Form.Field name="audienceName" className="form-field-spacing">
            <Form.Label><Text size="1">Audience Name</Text></Form.Label>
            <Form.Control asChild>
              <TextField.Root type="text" required placeholder="Enter audience name" />
            </Form.Control>
          </Form.Field>
        </>
      ) : (
        <>
          {audienceName && <input type="hidden" name="audienceName" value={audienceName} />}
          {audienceCategory && <input type="hidden" name="audienceCategory" value={audienceCategory} />}
        </>
      )}
      <input type="hidden" name="countrySelected" value={countrySelected} />
      {audienceCodes ? <input type="hidden" name="audienceCodes" value={audienceCodes} /> : null}
      <input type="hidden" name="requestType" value={requestType} />
      {audienceOverview ? <input type="hidden" name="audienceOverview" value={audienceOverview} /> : null}
      {audienceOverviewHtml ? <input type="hidden" name="audienceOverviewHtml" value={audienceOverviewHtml} /> : null}

      <Grid columns="2" gap="4">
        <Box>
          <Heading color="gray" as="h3" size="2">Contact Information</Heading>
          <hr />
          <Form.Field name="name">
            <Form.Label><Text size="1">Name</Text></Form.Label>
            <Form.Control asChild>
              <TextField.Root type="text" required placeholder="Enter your name" />
            </Form.Control>
          </Form.Field>

          <Form.Field name="email" className="form-field-spacing">
            <Form.Label><Text size="1">Email</Text></Form.Label>
            <Form.Control asChild>
              <TextField.Root
                type="email"
                required
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                placeholder="Enter your email"
              />
            </Form.Control>
            <Form.Message match="valueMissing">
              <Text color="red" size="1">Email is required</Text>
            </Form.Message>
            <Form.Message match="typeMismatch">
              <Text color="red" size="1">Please enter a valid email address</Text>
            </Form.Message>
            <Form.Message match="patternMismatch">
              <Text color="red" size="1">Please enter a valid email address</Text>
            </Form.Message>
          </Form.Field>

          <Form.Field name="company" className="form-field-spacing">
            <Form.Label><Text size="1">Company</Text></Form.Label>
            <Form.Control asChild>
              <TextField.Root type="text" required placeholder="Enter your company" />
            </Form.Control>
          </Form.Field>
        </Box>

        <Box>
          <Heading color="gray" as="h3" size="2">Campaign Information (Optional)</Heading>
          <hr />
          <Form.Field name="campaignStartDate">
            <Form.Label><Text size="1">Start Date</Text></Form.Label>
            <Form.Control asChild>
              <TextField.Root 
                type="date" 
                min={today}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Form.Control>
          </Form.Field>

          <Form.Field name="campaignEndDate" className="form-field-spacing">
            <Form.Label><Text size="1">End Date</Text></Form.Label>
            <Form.Control asChild>
              <TextField.Root 
                type="date" 
                min={startDate || today}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Form.Control>
          </Form.Field>

          {requestType === 'builder' ? (
            <Form.Field name="platformCode" className="form-field-spacing">
              <Form.Label><Text size="1">Platform (required)</Text></Form.Label>
              <Form.Control asChild>
                <TextField.Root
                  type="text"
                  required
                  placeholder="Enter platform"
                />
              </Form.Control>
            </Form.Field>
          ) : (
            <Form.Field name="platformCode" className="form-field-spacing">
              <Form.Label><Text size="1">Platform</Text></Form.Label>
              <Form.Control asChild>
                  <GenericSelect 
                    className='Input fullwidth'
                    items={platforms} 
                    selectedItem={selectedPlatform} 
                    onItemChange={(platform) => {
                      setPlatformError('');
                      onPlatformSelected(platform);
                    }} 
                    withSearch={false} 
                    sectionLabel="DSPs"
                    renderIcon={() => <DesktopIcon />}
                    otherOptions={{enabled: true, label: 'Other', value: 'other', position: 'top', items: [...otherDSPOptions]}}
                    emptyOption={{enabled: false}}
                    placeholder="Select a platform…"/>
              </Form.Control>
              {platformError ? <Form.Message><Text color="red" size="1">{platformError}</Text></Form.Message> : null}
            </Form.Field>
          )}

          <Form.Field name="adType" className="form-field-spacing">
            <Form.Label><Text size="1">Ad Type</Text></Form.Label>
            <Form.Control asChild>
              <TextField.Root type="text" placeholder="Enter Ad Type" />
            </Form.Control>
          </Form.Field>
        </Box>
      </Grid>

      {/* Seat Info */}
      <Box className="form-section">
        <Heading color="gray" as="h3" size="2">Seat Information</Heading>
        <hr />
        <Grid columns="2" gap="4">
          <Form.Field name="seatName">
            <Form.Label><Text size="1">Seat Name</Text></Form.Label>
            <Form.Control asChild>
              <TextField.Root type="text" required placeholder="Enter seat name" />
            </Form.Control>
          </Form.Field>

          <Form.Field name="seatId">
            <Form.Label><Text size="1">Seat ID</Text></Form.Label>
            <Form.Control asChild>
              <TextField.Root type="text" required placeholder="Enter seat ID" />
            </Form.Control>
          </Form.Field>
        </Grid>
      </Box>

      <Flex gap="2" mt="4">
        <Button variant="soft" type="button" onClick={onBack}><ArrowLeftIcon /></Button>
        <Form.Submit asChild>
          <Button color="green" style={{ flex: 1 }}>Submit</Button>
        </Form.Submit>
      </Flex>

      {successMessage && <Box mt="3"><Text color="green">{successMessage}</Text></Box>}
    </Form.Root>
  );
};

export default AudienceRequestForm;
